/**
 * ModalCadastrarProduto.jsx - Scriptus Pharma Partner
 * Modal dedicado para cadastro/edição de produtos
 * 
 * ✅ Componente separado e reutilizável
 * ✅ Validação de formulário
 * ✅ Preview em tempo real
 * ✅ Design Glassmorphism
 * ✅ BUG FIX: Dependências do useCallback corrigidas
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  X,
  Pill,
  Save,
  Loader2,
  DollarSign,
  Package,
  Eye,
  AlertTriangle
} from 'lucide-react';

// ============================================================================
// CONSTANTES
// ============================================================================
const FORMAS_FARMACEUTICAS = [
  'Comprimido', 'Cápsula', 'Comprimido Revestido', 'Solução Oral',
  'Suspensão Oral', 'Injetável', 'Pomada', 'Creme', 'Gel', 'Colírio',
  'Spray Nasal', 'Adesivo', 'Supositório', 'Pó para Solução', 'Xarope', 'Outros'
];

const CATEGORIAS = [
  'Cardiovascular', 'Diabetes', 'Emagrecimento', 'Antibiótico',
  'Anti-inflamatório', 'Analgésico', 'Antidepressivo', 'Ansiolítico',
  'Hormonal', 'Dermatológico', 'Oftalmológico', 'Respiratório',
  'Gastrointestinal', 'Vitaminas', 'Manipulado', 'Outros'
];

const FORM_INICIAL = {
  nome: '',
  principioAtivo: '',
  concentracao: '',
  formaFarmaceutica: '',
  preco: '',
  categoria: '',
  codigoEan: '',
  disponivel: true,
  observacoes: ''
};

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================
export default function ModalCadastrarProduto({ 
  visivel, 
  onFechar, 
  produto = null,
  onSalvar, 
  carregando = false,
  nomeFarmacia = 'Sua Farmácia'
}) {
  // ============================================================================
  // ESTADOS
  // ============================================================================
  const [form, setForm] = useState(FORM_INICIAL);
  const [erros, setErros] = useState({});
  const [touched, setTouched] = useState({});

  // ============================================================================
  // REFS
  // ============================================================================
  const mountedRef = useRef(true);
  const inputRef = useRef(null);

  // ============================================================================
  // EFEITOS
  // ============================================================================
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Preencher form quando produto é passado (edição)
  useEffect(() => {
    if (visivel) {
      if (produto) {
        setForm({
          nome: produto.nome || '',
          principioAtivo: produto.principioAtivo || '',
          concentracao: produto.concentracao || '',
          formaFarmaceutica: produto.formaFarmaceutica || '',
          preco: produto.preco?.toString() || '',
          categoria: produto.categoria || '',
          codigoEan: produto.codigoEan || '',
          disponivel: produto.disponivel !== false,
          observacoes: produto.observacoes || ''
        });
      } else {
        setForm(FORM_INICIAL);
      }
      setErros({});
      setTouched({});
      
      // Focus no primeiro input
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [produto, visivel]);

  // ============================================================================
  // VALIDAÇÃO
  // ============================================================================
  const validarCampo = useCallback((campo, valorAtual) => {
    // Usa o valor passado diretamente para evitar problemas de closure
    const valor = valorAtual !== undefined ? valorAtual : form[campo];
    
    setErros(prevErros => {
      const novosErros = { ...prevErros };
      
      switch (campo) {
        case 'nome':
          const nomeValor = typeof valor === 'string' ? valor : '';
          if (!nomeValor.trim()) {
            novosErros.nome = 'Nome é obrigatório';
          } else if (nomeValor.trim().length < 2) {
            novosErros.nome = 'Nome deve ter pelo menos 2 caracteres';
          } else {
            delete novosErros.nome;
          }
          break;
          
        case 'preco':
          const precoValor = typeof valor === 'string' ? valor : '';
          const precoNum = parseFloat(precoValor.replace(',', '.'));
          if (!precoValor) {
            novosErros.preco = 'Preço é obrigatório';
          } else if (isNaN(precoNum) || precoNum <= 0) {
            novosErros.preco = 'Preço deve ser maior que zero';
          } else {
            delete novosErros.preco;
          }
          break;
          
        default:
          break;
      }
      
      return novosErros;
    });
  }, [form]);

  // ============================================================================
  // HANDLERS
  // ============================================================================
  const handleChange = useCallback((campo, valor) => {
    setForm(prev => ({ ...prev, [campo]: valor }));
    setTouched(prev => ({ ...prev, [campo]: true }));
    
    // Limpar erro do campo quando usuário digita (validação será feita no blur)
    setErros(prev => {
      const newErros = { ...prev };
      delete newErros[campo];
      return newErros;
    });
  }, []);

  const handleBlur = useCallback((campo, valorAtual) => {
    setTouched(prev => ({ ...prev, [campo]: true }));
    // Passa o valor atual diretamente para evitar problemas de closure
    validarCampo(campo, valorAtual);
  }, [validarCampo]);

  // ============================================================================
  // VALIDAÇÃO DO FORMULÁRIO COMPLETO
  // ============================================================================
  const validarFormulario = useCallback(() => {
    const novosErros = {};
    
    // Nome
    if (!form.nome.trim()) {
      novosErros.nome = 'Nome é obrigatório';
    } else if (form.nome.trim().length < 2) {
      novosErros.nome = 'Nome deve ter pelo menos 2 caracteres';
    }
    
    // Preço
    const precoNum = parseFloat(form.preco.replace(',', '.'));
    if (!form.preco) {
      novosErros.preco = 'Preço é obrigatório';
    } else if (isNaN(precoNum) || precoNum <= 0) {
      novosErros.preco = 'Preço deve ser maior que zero';
    }
    
    setErros(novosErros);
    setTouched({ nome: true, preco: true });
    
    return Object.keys(novosErros).length === 0;
  }, [form]);

  // ============================================================================
  // SUBMIT
  // ============================================================================
  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    
    if (!validarFormulario()) {
      return;
    }
    
    const dadosParaSalvar = {
      ...form,
      preco: parseFloat(form.preco.replace(',', '.')) || 0
    };
    
    onSalvar(dadosParaSalvar);
  }, [form, validarFormulario, onSalvar]);

  // ============================================================================
  // FORMATAÇÃO
  // ============================================================================
  const formatarPreco = useCallback((valor) => {
    if (!valor) return 'R$ --';
    const num = parseFloat(valor.replace(',', '.'));
    if (isNaN(num)) return 'R$ --';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(num);
  }, []);

  // ============================================================================
  // RENDER
  // ============================================================================
  if (!visivel) return null;

  const isEdicao = Boolean(produto);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Overlay */}
      <div 
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onFechar}
      />
      
      {/* Modal */}
      <div className="modal-glass relative w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="modal-header flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="modal-icon">
              <Pill className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <h2 className="sf-pro-display font-bold text-slate-900 text-lg">
                {isEdicao ? 'Editar Produto' : 'Novo Produto'}
              </h2>
              <p className="sf-pro-text text-slate-500 text-xs">
                Visível para médicos em: <span className="font-semibold text-emerald-600">{nomeFarmacia}</span>
              </p>
            </div>
          </div>
          <button
            onClick={onFechar}
            disabled={carregando}
            className="modal-close"
            aria-label="Fechar modal"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Form - Scrollable */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto custom-scrollbar">
          <div className="p-5 space-y-4">
            {/* Nome do Medicamento */}
            <div>
              <label className="label-text">Medicamento e Dose *</label>
              <input
                ref={inputRef}
                type="text"
                value={form.nome}
                onChange={(e) => handleChange('nome', e.target.value)}
                onBlur={(e) => handleBlur('nome', e.target.value)}
                placeholder="Ex: Ozempic 1mg Caneta"
                disabled={carregando}
                maxLength={200}
                className={`input-glass w-full ${touched.nome && erros.nome ? 'input-error' : ''}`}
              />
              {touched.nome && erros.nome ? (
                <span className="error-text">
                  <AlertTriangle className="w-3 h-3" />
                  {erros.nome}
                </span>
              ) : (
                <span className="hint-text">{form.nome.length}/200 - Nome que aparecerá para os médicos</span>
              )}
            </div>

            {/* Princípio Ativo */}
            <div>
              <label className="label-text">Princípio Ativo</label>
              <input
                type="text"
                value={form.principioAtivo}
                onChange={(e) => handleChange('principioAtivo', e.target.value)}
                placeholder="Ex: Semaglutida"
                disabled={carregando}
                maxLength={100}
                className="input-glass w-full"
              />
              <span className="hint-text">Facilita a busca pelos médicos</span>
            </div>

            {/* Concentração */}
            <div>
              <label className="label-text">Concentração / Apresentação</label>
              <input
                type="text"
                value={form.concentracao}
                onChange={(e) => handleChange('concentracao', e.target.value)}
                placeholder="Ex: 1mg/dose - Caneta 3ml"
                disabled={carregando}
                maxLength={100}
                className="input-glass w-full"
              />
            </div>

            {/* Forma Farmacêutica e Categoria */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-text">Forma Farmacêutica</label>
                <select
                  value={form.formaFarmaceutica}
                  onChange={(e) => handleChange('formaFarmaceutica', e.target.value)}
                  disabled={carregando}
                  className="input-glass w-full"
                >
                  <option value="">Selecione...</option>
                  {FORMAS_FARMACEUTICAS.map(forma => (
                    <option key={forma} value={forma}>{forma}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label-text">Categoria</label>
                <select
                  value={form.categoria}
                  onChange={(e) => handleChange('categoria', e.target.value)}
                  disabled={carregando}
                  className="input-glass w-full"
                >
                  <option value="">Selecione...</option>
                  {CATEGORIAS.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Preço e Código EAN */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-text">Preço (R$) *</label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    value={form.preco}
                    onChange={(e) => {
                      const valor = e.target.value.replace(/[^\d,\.]/g, '');
                      handleChange('preco', valor);
                    }}
                    onBlur={(e) => handleBlur('preco', e.target.value)}
                    placeholder="0,00"
                    disabled={carregando}
                    className={`input-glass w-full pl-10 ${touched.preco && erros.preco ? 'input-error' : ''}`}
                  />
                </div>
                {touched.preco && erros.preco && (
                  <span className="error-text">
                    <AlertTriangle className="w-3 h-3" />
                    {erros.preco}
                  </span>
                )}
              </div>
              <div>
                <label className="label-text">Código EAN (opcional)</label>
                <input
                  type="text"
                  value={form.codigoEan}
                  onChange={(e) => handleChange('codigoEan', e.target.value.replace(/\D/g, ''))}
                  placeholder="7891234567890"
                  disabled={carregando}
                  maxLength={14}
                  className="input-glass w-full"
                />
              </div>
            </div>

            {/* Disponibilidade */}
            <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50/80 border border-slate-200/50">
              <div className="flex items-center gap-2">
                <Package className="w-4 h-4 text-slate-500" />
                <span className="sf-pro-text text-sm text-slate-700">Disponível em estoque</span>
              </div>
              <button
                type="button"
                onClick={() => handleChange('disponivel', !form.disponivel)}
                disabled={carregando}
                className={`toggle ${form.disponivel ? 'toggle-active' : ''}`}
                aria-label={form.disponivel ? 'Desativar disponibilidade' : 'Ativar disponibilidade'}
              >
                <span className="toggle-dot" />
              </button>
            </div>

            {/* Observações */}
            <div>
              <label className="label-text">Observações (opcional)</label>
              <textarea
                value={form.observacoes}
                onChange={(e) => handleChange('observacoes', e.target.value)}
                placeholder="Informações adicionais sobre o produto..."
                disabled={carregando}
                maxLength={500}
                rows={2}
                className="input-glass w-full resize-none"
              />
            </div>

            {/* Preview */}
            <div className="preview-card">
              <div className="flex items-center gap-2 mb-2">
                <Eye className="w-3.5 h-3.5 text-blue-600" />
                <span className="sf-pro-text text-xs font-semibold text-blue-800">
                  Preview - Como o médico verá:
                </span>
              </div>
              <div className="bg-white/80 rounded-lg p-3 border border-blue-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="sf-pro-text font-bold text-slate-900 text-sm">
                      {form.nome || 'Nome do medicamento'}
                    </p>
                    {form.principioAtivo && (
                      <p className="sf-pro-text text-slate-500 text-xs italic">
                        {form.principioAtivo}
                      </p>
                    )}
                    <p className="sf-pro-text text-slate-500 text-xs">
                      {nomeFarmacia} • {form.disponivel ? '✔ Em estoque' : '⏳ Sob encomenda'}
                    </p>
                  </div>
                  <span className="sf-pro-display font-bold text-emerald-600">
                    {formatarPreco(form.preco)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Footer - Fixed */}
          <div className="modal-footer flex-shrink-0">
            <button
              type="button"
              onClick={onFechar}
              disabled={carregando}
              className="button-secondary flex-1"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={carregando || !form.nome.trim() || !form.preco}
              className="button-primary flex-1"
            >
              {carregando ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Salvando...</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>{isEdicao ? 'Atualizar' : 'Cadastrar'}</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Styles */}
      <style>{`
        .sf-pro-display {
          font-family: -apple-system, "SF Pro Display", BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          -webkit-font-smoothing: antialiased;
        }
        .sf-pro-text {
          font-family: -apple-system, "SF Pro Text", BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          -webkit-font-smoothing: antialiased;
        }
        .modal-glass {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(30px);
          border: 1px solid rgba(255, 255, 255, 0.6);
          border-radius: 24px;
          box-shadow: 0 25px 80px rgba(0, 0, 0, 0.15);
        }
        .modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 20px;
          border-bottom: 1px solid rgba(0, 0, 0, 0.05);
        }
        .modal-icon {
          width: 44px;
          height: 44px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(16, 185, 129, 0.1);
          border-radius: 12px;
          border: 1px solid rgba(16, 185, 129, 0.2);
        }
        .modal-close {
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(0, 0, 0, 0.05);
          border-radius: 8px;
          color: #64748b;
          transition: all 0.2s ease;
        }
        .modal-close:hover:not(:disabled) {
          background: rgba(239, 68, 68, 0.1);
          color: #dc2626;
        }
        .modal-close:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .modal-footer {
          display: flex;
          gap: 12px;
          padding: 16px 20px;
          border-top: 1px solid rgba(0, 0, 0, 0.05);
          background: rgba(255, 255, 255, 0.8);
        }
        .input-glass {
          padding: 12px 14px;
          background: rgba(255, 255, 255, 0.85);
          border: 2px solid rgba(255, 255, 255, 0.6);
          border-radius: 12px;
          backdrop-filter: blur(16px);
          transition: all 0.3s ease;
          font-size: 14px;
          color: #1e293b;
        }
        .input-glass::placeholder { color: #94a3b8; }
        .input-glass:focus {
          outline: none;
          background: rgba(255, 255, 255, 0.95);
          border-color: rgba(16, 185, 129, 0.6);
          box-shadow: 0 0 0 4px rgba(16, 185, 129, 0.1);
        }
        .input-glass:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .input-glass.input-error {
          border-color: rgba(239, 68, 68, 0.6);
        }
        .input-glass.input-error:focus {
          box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.1);
        }
        .label-text {
          display: block;
          font-size: 11px;
          font-weight: 600;
          color: #64748b;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-bottom: 6px;
        }
        .hint-text {
          display: block;
          font-size: 11px;
          color: #94a3b8;
          margin-top: 4px;
        }
        .error-text {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 11px;
          color: #dc2626;
          margin-top: 4px;
        }
        .toggle {
          width: 44px;
          height: 24px;
          background: #e2e8f0;
          border-radius: 12px;
          position: relative;
          transition: background 0.3s ease;
          cursor: pointer;
          border: none;
        }
        .toggle:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .toggle.toggle-active {
          background: #10b981;
        }
        .toggle-dot {
          position: absolute;
          top: 2px;
          left: 2px;
          width: 20px;
          height: 20px;
          background: white;
          border-radius: 50%;
          transition: transform 0.3s ease;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }
        .toggle.toggle-active .toggle-dot {
          transform: translateX(20px);
        }
        .preview-card {
          background: rgba(59, 130, 246, 0.05);
          border: 1px solid rgba(59, 130, 246, 0.2);
          border-radius: 12px;
          padding: 12px;
        }
        .button-primary {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 12px 20px;
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          border: none;
          border-radius: 12px;
          color: white;
          font-weight: 600;
          font-size: 14px;
          transition: all 0.3s ease;
          box-shadow: 0 10px 32px rgba(16, 185, 129, 0.25);
          cursor: pointer;
        }
        .button-primary:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 16px 40px rgba(16, 185, 129, 0.3);
        }
        .button-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }
        .button-secondary {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          padding: 12px 20px;
          background: rgba(255, 255, 255, 0.85);
          backdrop-filter: blur(16px);
          border: 2px solid rgba(255, 255, 255, 0.6);
          border-radius: 12px;
          color: #475569;
          font-weight: 600;
          font-size: 14px;
          transition: all 0.3s ease;
          cursor: pointer;
        }
        .button-secondary:hover:not(:disabled) {
          background: rgba(255, 255, 255, 0.95);
          color: #1e293b;
        }
        .button-secondary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(241, 245, 249, 0.5);
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(203, 213, 225, 0.8);
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(148, 163, 184, 0.9);
        }
        @media (max-width: 768px) {
          .input-glass {
            font-size: 16px;
          }
        }
      `}</style>
    </div>
  );
}